import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Car, Lock, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.message === "Network Error") {
        setError("Erreur reseau - verifiez votre connexion internet");
      } else {
        setError("Erreur de connexion - reessayez");
      }
      console.error("Login error:", err.message, err.response?.status, err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <Car size={36} weight="bold" className="text-blue-400" />
            <span className="text-white font-extrabold text-2xl tracking-tight">MikAuto</span>
            <span className="text-overline text-blue-400">Belgium</span>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl font-extrabold text-white leading-tight tracking-tight">
            Gérez votre garage<br />en toute simplicité
          </h1>
          <p className="text-slate-400 mt-4 text-lg">
            Stock véhicules, préparation, CRM, facturation et plus encore.
          </p>
        </div>
        <div className="text-slate-600 text-sm relative z-10">
          &copy; 2026 MikAuto Belgium. Tous droits réservés.
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Car size={32} weight="bold" className="text-blue-600" />
            <span className="text-slate-900 dark:text-slate-100 font-extrabold text-xl tracking-tight">MikAuto Belgium</span>
          </div>

          <div className="card p-8">
            <h2 className="text-heading-2 text-slate-900 dark:text-slate-100 text-center">Connexion</h2>
            <p className="text-slate-500 dark:text-slate-400 text-center mt-2 text-body-sm">
              Connectez-vous à votre espace de gestion
            </p>

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <Label className="form-label">Email</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="login-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">Mot de passe</Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="login-password"
                    type="password"
                    placeholder="Votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-6 font-semibold"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>

            <p className="text-caption text-slate-400 text-center mt-6">
              Contactez l'administrateur pour obtenir un compte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
